import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, patch, post } from "../../lib/request"
import { AI_BASE_URL, aiApiKeyHeaders } from "../../lib/aiApiBaseUrl"
import { deleteMedia } from "./mediaDeleteSlice"
import { pickProfileAvatarUrl } from "../../lib/utils/userProfileAvatar"

export const fetchUserProfile = createAsyncThunk(
  "user/fetchProfile",
  async (_arg, { rejectWithValue }) => {
    const MAX_RETRIES = 2
    let lastError = null

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const data = await get("users/me")
        return data
      } catch (err) {
        lastError = err
        console.error(`Profile fetch attempt ${attempt + 1} failed:`, err?.message)

        if (err?.message?.includes("Authentication not ready") && attempt < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)))
          continue
        }

        if (err?.message?.includes("401") || err?.message?.includes("Session expired")) {
          return rejectWithValue(err?.message || "Session expired. Please login again.")
        }

        if (attempt < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)))
        }
      }
    }

    return rejectWithValue(lastError?.message || "Failed to fetch user profile")
  },
  {
    condition: (arg, { getState }) => {
      if (arg === true || arg?.force) return true
      const { status, profile } = getState().user ?? {}
      if (status === "loading") return false
      if (profile && status === "succeeded") return false
      return true
    },
  },
)

export const updateUserProfile = createAsyncThunk(
  "user/updateProfile",
  async ({ id, ...profileData }, { rejectWithValue }) => {
    try {
      const data = await patch(`users/${id}`, profileData)
      return data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update profile")
    }
  },
)

export const updateUserPhoto = createAsyncThunk(
  "user/updatePhoto",
  async ({ id, file }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData()
      formData.append("file", file)

      await patch(`users/${id}/photo`, formData)
      const freshProfile = await dispatch(fetchUserProfile({ force: true })).unwrap()
      return freshProfile
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update profile photo")
    }
  },
)

export const fetchUserProfileAfterLogin = createAsyncThunk(
  "user/fetchProfileAfterLogin",
  async (_, { getState, rejectWithValue }) => {
    const MAX_RETRIES = 2
    let lastError = null

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const data = await get("users/me")
        return data
      } catch (err) {
        lastError = err
        console.error(` Profile fetch after login attempt ${attempt + 1} failed:`, err?.message)

        if (err?.message?.includes("401") || err?.message?.includes("Session expired")) {
          return rejectWithValue(err?.message || "Authentication failed")
        }

        if (attempt < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)))
        }
      }
    }

    return rejectWithValue(lastError?.message || "Failed to fetch user profile")
  },
)

export const fetchUserMedias = createAsyncThunk(
  "user/fetchUserMedias",
  async (
    { clientId, query, media_type, tags, resolution_category, primary_category, mode, keepExistingDuringLoad } = {},
    { rejectWithValue },
  ) => {
    try {
      const base = `${AI_BASE_URL}/clients/${clientId}/media`

      // const base = `https://api-login-dev.rategainsoho.com/clients/${clientId}/media`
      const url = new URL(base)
      if (query) url.searchParams.set("query", query)
      if (mode) url.searchParams.set("mode", mode)
      if (media_type) url.searchParams.set("media_type", media_type)
      if (tags) url.searchParams.set("tags", Array.isArray(tags) ? tags.join(",") : tags)
      if (resolution_category) url.searchParams.set("resolution_category", resolution_category)
      if (primary_category) url.searchParams.set("primary_category", primary_category)

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...aiApiKeyHeaders(),
          ...(localStorage.getItem("token") && {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          }),
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch media`)
      }

      const data = await response.json()
      return data
    } catch (err) {
      return rejectWithValue(err.message || "Failed to load user medias")
    }
  },
  {
    condition: (arg = {}, { getState }) => {
      if (arg?.force) return true
      if (arg?.query || arg?.media_type || arg?.tags || arg?.mode) return true
      const clientId = arg?.clientId
      if (clientId == null || clientId === "") return false
      const user = getState().user ?? {}
      const sameClient = String(user.mediasClientId ?? "") === String(clientId)
      if (user.mediasStatus === "loading" && sameClient) return false
      if (user.mediasStatus === "succeeded" && sameClient) return false
      return true
    },
  },
)

export const changePassword = createAsyncThunk(
  "user/changePassword",
  async ({ username, oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const endpoint =
        `/users/change-password` +
        `?username=${encodeURIComponent(username)}` +
        `&old_password=${encodeURIComponent(oldPassword)}` +
        `&new_password=${encodeURIComponent(newPassword)}`;

      const response = await post(endpoint, null);
     

      return response;
    } catch (err) {      
      if (err?.response?.data) {
        return rejectWithValue(err.response.data);
      }

      if (err?.response?.status === 403) {
        return rejectWithValue("Current password is incorrect.");
      }

      return rejectWithValue("Invalid old password.");


    }
  }
);


const initialState = {
  profile: null,
  medias: [],
  mediasRequestId: null,
  /** clientId of the last successful full library fetch — avoids duplicate /media when landing + details both mount */
  mediasClientId: null,
  /** Total asset count from media list API (`count`), when present — may exceed paginated `results.length` */
  mediasTotalCount: null,
  searchSuggestions: [], // from media API response search_suggestions
  /** Last media list `query` from fetchUserMedias (empty when browsing the full library). */
  mediasQuery: "",
  status: "idle",
  mediasStatus: "idle",
  mediasError: null, // media API errors only; do not use for profile
  error: null,
  updateStatus: "idle",
  updateError: null,
  passwordStatus: "idle",
  passwordError: null,
}

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearUserProfile: (state) => {
      state.profile = null
      state.medias = []
      state.mediasClientId = null
      state.mediasTotalCount = null
      state.searchSuggestions = []
      state.mediasQuery = ""
      state.status = "idle"
      state.mediasStatus = "idle"
      state.mediasError = null
      state.error = null
      state.updateStatus = "idle"
      state.updateError = null
      state.passwordStatus = "idle"
      state.passwordError = null
    },
    clearUserMedias: (state) => {
      state.medias = []
      state.mediasClientId = null
      state.mediasTotalCount = null
      state.searchSuggestions = []
      state.mediasQuery = ""
      state.mediasStatus = "idle"
      state.mediasError = null
    },
    clearUserError: (state) => {
      state.error = null
      state.updateError = null
    },
    clearUpdateStatus: (state) => {
      state.updateStatus = "idle"
      state.updateError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        if (!state.profile) {
          state.status = "loading"
        }
        state.error = null
      })

      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.profile = action.payload
        state.error = null
      })

      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.updateStatus = "loading"
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.updateStatus = "succeeded"
        if (!state.profile) return
        const { id: _id, ...requested } = action.meta.arg || {}
        const fromApi =
          action.payload && typeof action.payload === "object" ? action.payload : {}
        state.profile = { ...state.profile, ...requested, ...fromApi }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.updateStatus = "failed"
        state.updateError = action.payload
      })
      .addCase(updateUserPhoto.pending, (state) => {
        state.updateStatus = "loading"
      })
      .addCase(updateUserPhoto.fulfilled, (state, action) => {
        state.updateStatus = "succeeded"
        if (!state.profile) return
        const avatarCacheBust = Date.now()
        const payload = action.payload
        if (payload && typeof payload === "object") {
          const nextAvatar = pickProfileAvatarUrl(payload) || state.profile.avatar
          state.profile = {
            ...state.profile,
            ...payload,
            avatar: nextAvatar,
            avatarCacheBust,
          }
          return
        }
        state.profile = { ...state.profile, avatarCacheBust }
      })
      .addCase(updateUserPhoto.rejected, (state, action) => {
        state.updateStatus = "failed"
        state.updateError = action.payload
      })
      .addCase(fetchUserProfileAfterLogin.pending, (state) => {
        state.status = "loading"
      })
      .addCase(fetchUserProfileAfterLogin.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.profile = action.payload
        state.error = null
      })

      .addCase(fetchUserProfileAfterLogin.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
      .addCase(fetchUserMedias.pending, (state, action) => {
        state.mediasStatus = "loading"
        state.mediasRequestId = action.meta.requestId
        const nextClientId = action.meta.arg?.clientId
        // Avoid showing the previous account/client's library while the new request is in flight
        if (!action.meta.arg?.keepExistingDuringLoad) {
          state.medias = []
          state.searchSuggestions = []
          state.mediasTotalCount = null
        }
        if (nextClientId != null) state.mediasClientId = nextClientId
        state.mediasError = null
      })
      .addCase(fetchUserMedias.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.mediasRequestId) return

        state.mediasStatus = "succeeded"
        state.medias = action.payload?.results || []
        state.mediasQuery = String(action.meta?.arg?.query || "").trim()
        state.searchSuggestions = Array.isArray(action.payload?.search_suggestions)
          ? action.payload.search_suggestions
          : []
        const cid = action.meta?.arg?.clientId
        if (cid != null) state.mediasClientId = cid
        const rawCount = action.payload?.count
        const resultsLen = Array.isArray(action.payload?.results) ? action.payload.results.length : 0
        state.mediasTotalCount =
          typeof rawCount === "number" && !Number.isNaN(rawCount) ? rawCount : resultsLen
      })
      .addCase(fetchUserMedias.rejected, (state, action) => {
        if (action.meta.requestId !== state.mediasRequestId) return

        state.mediasStatus = "failed"
        state.mediasError = action.payload

        const cid = action.meta?.arg?.clientId
        if (cid != null) state.mediasClientId = cid
      })
      .addCase(deleteMedia.fulfilled, (state, action) => {
        const rawIds = action.payload?.mediaIds
        if (!Array.isArray(rawIds) || rawIds.length === 0) return
        const idSet = new Set(rawIds.map((id) => String(id)))
        const removedCount = state.medias.filter(
          (m) => m?.id_media != null && idSet.has(String(m.id_media)),
        ).length
        state.medias = state.medias.filter(
          (m) => m?.id_media == null || !idSet.has(String(m.id_media)),
        )
        if (state.mediasTotalCount != null && removedCount > 0) {
          state.mediasTotalCount = Math.max(0, state.mediasTotalCount - removedCount)
        }
      })
      .addCase(changePassword.pending, (state) => {
        state.passwordStatus = "loading"
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.passwordStatus = "succeeded"
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.passwordStatus = "failed"
        state.passwordError = action.payload
      })
  },
})

export const { clearUserProfile, clearUserMedias, clearUserError, clearUpdateStatus } = userSlice.actions

export default userSlice.reducer
